import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Printer, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchInvoicePdfBlob, invoicePdfFilename } from "@/lib/invoiceDoc";

// Large centered modal that displays the ACTUAL printable A4 invoice document
// (the backend-rendered PDF) — distinct from the quick right-side details
// drawer. Print / Download / Open-in-new-tab all use the same PDF bytes.
export default function InvoicePreviewModal({ invoiceId, invoiceNumber, open, onOpenChange }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const blobRef = useRef(null);

  const cleanup = useCallback(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!open || !invoiceId) return undefined;
    let revoked = false;
    let localUrl = null;
    setLoading(true);
    setError(null);
    fetchInvoicePdfBlob(invoiceId, "digital")
      .then((blob) => {
        if (revoked) return;
        blobRef.current = blob;
        localUrl = URL.createObjectURL(blob);
        setBlobUrl(localUrl);
      })
      .catch(() => {
        if (!revoked) setError("Could not load the invoice preview.");
      })
      .finally(() => {
        if (!revoked) setLoading(false);
      });
    return () => {
      revoked = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [open, invoiceId]);

  useEffect(() => {
    if (!open) cleanup();
  }, [open, cleanup]);

  const handlePrint = () => {
    const frame = iframeRef.current;
    try {
      if (frame && frame.contentWindow) {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        return;
      }
    } catch {
      /* cross-context print blocked — fall through to new-tab print */
    }
    if (blobUrl) {
      const w = window.open(blobUrl, "_blank");
      if (w) w.addEventListener("load", () => w.print());
    }
  };

  const handleDownload = async () => {
    try {
      const blob = blobRef.current || (await fetchInvoicePdfBlob(invoiceId, "digital"));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = invoicePdfFilename(invoiceNumber, invoiceId);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleOpenTab = () => {
    window.open(`/invoices/${invoiceId}/preview`, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invoice-preview-modal"
        className="max-w-[920px] w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col bg-[#F2F0EB]"
      >
        <DialogHeader className="px-5 py-3.5 border-b border-[#E1DDD3] bg-white flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="text-base font-display tracking-tight text-ink">Invoice Preview</DialogTitle>
            <DialogDescription className="text-xs text-ink-soft mt-0.5">
              {invoiceNumber ? `${invoiceNumber} — ` : ""}exactly what will be printed & downloaded
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2 mr-7">
            <button data-testid="invoice-preview-print" onClick={handlePrint}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-[#DDD8CC] hover:bg-linen text-ink transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button data-testid="invoice-preview-download" onClick={handleDownload}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-[#DDD8CC] hover:bg-linen text-ink transition-colors">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button data-testid="invoice-preview-open-tab" onClick={handleOpenTab}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Open <span className="ml-0.5">↗</span>
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto grid place-items-center p-4">
          {loading && (
            <div className="flex flex-col items-center gap-3 text-ink-mid" data-testid="invoice-preview-loading">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">Rendering invoice…</span>
            </div>
          )}
          {error && !loading && (
            <div className="text-sm text-clay" data-testid="invoice-preview-error">{error}</div>
          )}
          {blobUrl && !loading && !error && (
            <iframe
              ref={iframeRef}
              data-testid="invoice-preview-frame"
              title="Invoice document"
              src={blobUrl}
              className="w-full h-full min-h-[70vh] bg-white shadow-lg rounded-sm border-0"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
